import { ReactElement, useMemo } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import {
  defaultLayoutPlugin,
  ToolbarProps,
  ToolbarSlot,
} from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { RenderCurrentPageLabelProps } from "@react-pdf-viewer/page-navigation";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import nextPage from "../../assets/nextPage.svg";
import prePage from "../../assets/prePage.svg";
import "./PdfViewer.scss";
import { BasicIcon } from "@pickit/pickit-components";
import { PdfViewerPropsTypes } from "./types";
import { GlobalWorkerOptions } from 'pdfjs-dist';
import * as pdfjsLib from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.js`;
export const PdfViewer = (props: PdfViewerPropsTypes) => {
  const renderToolbar = (Toolbar: (props: ToolbarProps) => ReactElement) => (
    <Toolbar>
      {(slots: ToolbarSlot) => {
        const {
          CurrentPageLabel,
          GoToNextPage,
          GoToPreviousPage,
          ZoomIn,
          ZoomOut,
        } = slots;
        return (
          <div className="toolbar-container">
            <div className="zoom-container">
              <div className="zoom-button">
                <ZoomOut>
                  {(props) => (
                    <BasicIcon
                      name="zoom-out"
                      data-testId="pdf-viewer-zoom-out-button"
                      onClick={props.onClick}
                    />
                  )}
                </ZoomOut>
              </div>
              <div>
                <ZoomIn>
                  {(props) => (
                    <BasicIcon
                      name="zoom-in"
                      data-testId="pdf-viewer-zoom-in-button"
                      onClick={props.onClick}
                    />
                  )}
                </ZoomIn>
              </div>
            </div>
            <div className="page-navigator-container">
              <div className="page-navigator">
                <GoToPreviousPage>
                  {(props) => (
                    <img
                      data-testId="pdf-viewer-previus-page-button"
                      onClick={props.onClick}
                      src={prePage}
                      alt="previus page button"
                    />
                  )}
                </GoToPreviousPage>
              </div>
              <div className="page-number">
                <CurrentPageLabel>
                  {(props: RenderCurrentPageLabelProps) => (
                    <span data-testId="pdf-viewer-current-page">{`${
                      props.currentPage + 1
                    } / ${props.numberOfPages}`}</span>
                  )}
                </CurrentPageLabel>
              </div>
              <div className="page-navigator">
                <GoToNextPage>
                  {(props) => (
                    <img
                      data-testId="pdf-viewer-next-page-button"
                      onClick={props.onClick}
                      src={nextPage}
                      alt="next page button"
                    />
                  )}
                </GoToNextPage>
              </div>
            </div>
            <div className="buttons-container">
              {props.children.map((child: ReactElement, index: number) => {
                return (
                  <div key={index} className="child-button">
                    {child}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }}
    </Toolbar>
  );

  const toolbarPluginInstance = toolbarPlugin();

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar,
  });

  const plugins = useMemo(
    () => [defaultLayoutPluginInstance, toolbarPluginInstance],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.src]
  );

  return (
    <div className="pdf-container">
      {props.src && (
        <>
          <Worker workerUrl={GlobalWorkerOptions.workerSrc}>
            <Viewer fileUrl={props.src} plugins={plugins} />
          </Worker>
        </>
      )}
    </div>
  );
};
